import { useState, useRef, useEffect } from "react";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription
} from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger
} from "../ui/dialog";
import {
  Plus, Pencil, Trash2, ChevronLeft, ChevronRight,
  Clock, CheckCircle, XCircle, Loader2
} from "lucide-react";

import { toast } from "sonner";
import ConfirmAlertDialog from "../modals/ConfirmAlertDialog";
import SelectCompset from "../select/SelectCompset";
import DatePicker from "../DatePicker";
import TimePicker from "../TimePicker";
import RequiredFormField from "../RequiredFormField";
import { onObjectInputChange } from "@/helpers/inputChange";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/services/queryKeyFactory";
import { logsServices } from "@/services/logs.service";
import { API_URLS } from "@/constants/urls";
import { useCompset } from "@/contexts/CompsetContext";
import { convertTo12Hour, convertTo24HourWithSeconds, formatDateMD, formatDateYMD } from "@/helpers/formatters";
import { apiClient } from "@/services/apiClient";
import { compareAsc, compareDesc } from "date-fns";
import { SCHEDULE_STATUS_MAP } from "@/constants";
import { apiErrorHandler } from "@/helpers/apiErrorHandler";
import { error } from "three";
import { usePlans } from "@/contexts/PlanRulesContext";
import DashboardError from "../error/DashboardError";
import FeatureRestrict from "./FeatureRestrict";

const ScheduledRuns = () => {
  const queryClient = useQueryClient();

  const [currentPage, setCurrentPage] = useState(1);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteScheduleStatus, setDeleteScheduleStatus] = useState(false);

  const { canCreateSchedule } = usePlans();


  const selectedScheduleRef = useRef(null);
  const { compsets } = useCompset();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    compset_name: "",
    start_date: "",
    expiration_date: "",
    run_time: "08:00 AM",
    active: true,
  });


  const itemsPerPage = 10;

  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.schedules(),
    queryFn: logsServices.getSchedules,
    staleTime: 5 * 60 * 1000,
  });

  const schedules = data?.data || [];

  // Toggle Active
  const handleToggleActive = async (scheduleId, newValue) => {

    toast.loading(newValue ? "Activating schedule" : "Deactivating schedule", {
      id: "status-update"
    })

    try {
      await apiClient.put(API_URLS.TOGGLE_SCHEDULE, {
        s_id: scheduleId
      });

      await queryClient.invalidateQueries({
        queryKey: queryKeys.schedules()
      });

      toast.success(`Schedule ${newValue ? "activated" : "deactivated"}`);
    } catch (error) {
      apiErrorHandler(error)
    } finally {
      toast.dismiss("status-update")
    }
  };

  // Delete
  const handleDeleteClick = (scheduleId) => {
    selectedScheduleRef.current = scheduleId;
    setDeleteScheduleStatus(true);
  };

  const handleDelete = async () => {
    setLoading(true)
    try {
      await apiClient.delete(API_URLS.REMOVE_SCHEDULE, {
        params: {
          scheduleId: selectedScheduleRef.current
        }
      });

      await queryClient.invalidateQueries({
        queryKey: queryKeys.schedules()
      });

      toast.success("Schedule deleted");
    } catch (error) {
      apiErrorHandler(error)
    } finally {
      setDeleteScheduleStatus(false);
      setLoading(false)
    }
  };

  // Edit
  const handleEdit = (schedule) => {

    setEditingSchedule(schedule);
    setFormData({
      compset_name: schedule?.id,
      start_date: new Date(schedule?.startDate),
      expiration_date: new Date(schedule?.endDate),
      run_time: convertTo12Hour(schedule?.runTime),
      active: schedule?.activeStatus,
    });

    setTimeout(() => {
      setIsAddDialogOpen(true);
    }, 0)

  };

  const handleSubmit = async () => {

    if (
      !formData.compset_name ||
      !formData.start_date ||
      !formData.expiration_date
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    const isBefore = compareDesc(formData.start_date, formData.expiration_date)


    if (isBefore == -1) {
      toast.error("Expiry date cannot be past date of start date");
      return
    }

    const compset = compsets?.user_compsets?.find(
      (comp) => comp?.compset_id == formData?.compset_name
    );

    if (!compset) {
      toast.error("Unable to find compset");
      setSaving(false);
      return;
    }

    setSaving(true);

    try {
      if (editingSchedule) {
        await apiClient.put(`/api/edit/schedule/`, {
          rateshopId: editingSchedule?.id,
          scheduleId: editingSchedule?.scheduleId,
          runTime: convertTo24HourWithSeconds(formData?.run_time),
          startDate: formatDateYMD(formData?.start_date),
          endDate: formatDateYMD(formData?.expiration_date)
        });
        toast.success("Schedule updated");
      } else {
        await apiClient.post(API_URLS.CREATE_SCHEDULE, {
          rateshopId: formData?.compset_name,
          rateshopName: compset?.compset_name,
          startDate: formatDateYMD(formData?.start_date),
          endDate: formatDateYMD(formData?.expiration_date),
          runTime: convertTo24HourWithSeconds(formData?.run_time),
        });
        toast.success("Schedule created");
      }

      await queryClient.invalidateQueries({
        queryKey: queryKeys.schedules()
      });

      setIsAddDialogOpen(false);
      setEditingSchedule(null);
      setFormData({
        compset_name: "",
        start_date: "",
        expiration_date: "",
        run_time: "08:00 AM",
        active: true,
      });
    } catch (error) {
      apiErrorHandler(error)
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 0:
        return (
          <Badge className=" bg-gray-100 text-gray-700">
            <CheckCircle className="w-3 h-3 mr-1" />
            {SCHEDULE_STATUS_MAP[status].label}
          </Badge>
        );
      case 200:
        return (
          <Badge className="bg-emerald-100 text-emerald-700">
            <Clock className="w-3 h-3 mr-1" />
            {SCHEDULE_STATUS_MAP[status].label}
          </Badge>
        );
      case 400:
        return (
          <Badge className="bg-amber-100 text-amber-700">
            <XCircle className="w-3 h-3 mr-1" />
            {SCHEDULE_STATUS_MAP[status].label}
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  // Pagination
  const totalPages = Math.ceil(schedules?.length / itemsPerPage);
  const totalItems = schedules?.length;

  const paginatedSchedules = schedules.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );


  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages || 1);
    }
  }, [totalPages]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!canCreateSchedule()) {
    return <FeatureRestrict />
  }

  if (isError) {
    return (
      <DashboardError
        title="Unable to load Schedules"
        section="schedules"
      />
    );
  }

  return (
    <div className="space-y-6 min-h-[75vh]">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Scheduled Runs</h1>
          <p className="text-muted-foreground">
            Manage automated CompSet schedules
          </p>
        </div>

        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            {compsets && canCreateSchedule() && (
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Add Schedule
              </Button>
            )}
          </DialogTrigger>

          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingSchedule ? "Edit Schedule" : "Create Schedule"}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div>
                <Label>
                  Select Compset <RequiredFormField />
                </Label>
                <SelectCompset
                  value={formData.compset_name}
                  onChange={(value) =>
                    onObjectInputChange(
                      "compset_name",
                      value,
                      setFormData
                    )
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <DatePicker
                  value={formData.start_date}
                  onChange={(date) =>
                    onObjectInputChange(
                      "start_date",
                      date,
                      setFormData
                    )
                  }
                />

                <DatePicker
                  value={formData.expiration_date}
                  onChange={(date) =>
                    onObjectInputChange(
                      "expiration_date",
                      date,
                      setFormData
                    )
                  }
                />
              </div>

              <TimePicker
                value={formData.run_time}
                onChange={(time) =>
                  onObjectInputChange(
                    "run_time",
                    time,
                    setFormData
                  )
                }
              />

              <div className="flex justify-between">
                <Label>Active</Label>
                <Switch
                  checked={formData.active}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({
                      ...prev,
                      active: checked,
                    }))
                  }
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setIsAddDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={saving}>
                {saving && (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                )}
                {editingSchedule ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Schedules List</CardTitle>
          <CardDescription>
            Manage automated CompSet runs with their schedule, status, and execution settings.
          </CardDescription>
        </CardHeader>
        <CardContent>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Active</TableHead>
                <TableHead>Start Date</TableHead>
                <TableHead>End Date</TableHead>
                <TableHead>Created </TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Delete</TableHead>
                <TableHead>Edit</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {paginatedSchedules?.map((schedule) => (
                <TableRow key={schedule?.id}>
                  <TableCell>{schedule?.rateshopName}</TableCell>

                  <TableCell>
                    <Switch
                      checked={!!schedule?.activeStatus}
                      onCheckedChange={(checked) =>
                        handleToggleActive(schedule?.scheduleId, checked)
                      }
                    />
                  </TableCell>

                  <TableCell>{schedule?.startDate}</TableCell>
                  <TableCell>{schedule?.endDate}</TableCell>
                  <TableCell>{formatDateMD(schedule?.created)}</TableCell>
                  {/* <TableCell>{formatDateMD(schedule?.created)}</TableCell> */}

                  <TableCell>
                    {getStatusBadge(schedule?.status)}
                  </TableCell>

                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() =>
                        handleDeleteClick(schedule?.scheduleId)
                      }
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </TableCell>

                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() =>
                        handleEdit(schedule)
                      }
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {paginatedSchedules?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No schedules found. Add Schedule to automate your analysis.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {paginatedSchedules?.length > 0 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <span className="text-sm text-muted-foreground">
                {(currentPage - 1) * itemsPerPage + 1} to{" "}
                {Math.min(currentPage * itemsPerPage, totalItems)} of{" "}
                {totalItems}
              </span>

              <div className="flex items-center gap-2">

                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                  <ChevronLeft className="w-4 h-4 -ml-2" />
                </Button>

                <Button
                  variant="outline"
                  size="icon"
                  onClick={() =>
                    setCurrentPage((prev) =>
                      Math.max(1, prev - 1)
                    )
                  }
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>

                <span className="text-sm px-2">
                  Page {currentPage} of {totalPages || 1}
                </span>

                <Button
                  variant="outline"
                  size="icon"
                  onClick={() =>
                    setCurrentPage((prev) =>
                      Math.min(totalPages, prev + 1)
                    )
                  }
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>

                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                  <ChevronRight className="w-4 h-4 -ml-2" />
                </Button>

              </div>
            </div>
          )}

        </CardContent>
      </Card>

      <ConfirmAlertDialog
        open={deleteScheduleStatus}
        onClose={() => setDeleteScheduleStatus(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default ScheduledRuns;